import { AnyAction, configureStore, Reducer } from '@reduxjs/toolkit';

import authReducer from './slices/authSlice';
import goalsReducer from './slices/goalsSlice';
import portfolioReducer from './slices/portfolioSlice';
import userPreferencesReducer from './slices/userPreferencesSlice';

/**
 * Configuration for persisting Redux state
 */
export interface PersistConfig {
  key: string;
  storage: unknown;
  whitelist?: string[];
  blacklist?: string[];
}

/**
 * Platform-specific storage adapter interface
 */
export interface StorageAdapter {
  /**
   * Wraps a reducer with persistence logic if needed
   * @param reducer - The reducer to wrap
   * @param config - Persistence configuration
   * @returns The wrapped or original reducer
   */
  wrapReducer<S = unknown, A extends AnyAction = AnyAction>(
    reducer: Reducer<S, A>,
    config: PersistConfig
  ): Reducer<S, A>;

  /**
   * Returns middleware configuration for persistence
   */
  getMiddlewareConfig: () => {
    serializableCheck?: {
      ignoredActions?: string[];
      ignoredActionPaths?: string[];
      ignoredPaths?: string[];
    };
  };

  /**
   * Creates a persistor instance if needed (for mobile)
   */
  createPersistor?: (store: AppStore) => unknown;
}

/**
 * Configuration for creating a Redux store
 */
export interface StoreConfig {
  storageAdapter: StorageAdapter;
  additionalReducers?: Record<string, Reducer>;
  persistAuth?: boolean;
  persistGoals?: boolean;
  persistPortfolio?: boolean;
  persistUserPreferences?: boolean;
}

/**
 * Factory function to create a Redux store with platform-specific configurations
 * This allows web and mobile to share the same store logic while applying
 * different persistence strategies
 */
export function createAppStore(config: StoreConfig) {
  const {
    storageAdapter,
    additionalReducers = {},
    persistAuth = false,
    persistGoals = false,
    persistPortfolio = false,
    persistUserPreferences = false,
  } = config;

  // Prepare reducers with optional persistence
  const authReducerFinal = persistAuth
    ? storageAdapter.wrapReducer(authReducer, {
        key: 'auth',
        storage: null, // Will be provided by adapter
        whitelist: [
          'user',
          'isAuthenticated',
          'hasGoals',
          'isOnboardingComplete',
          'coreOnboardingComplete',
          'session',
        ],
      })
    : authReducer;

  const goalsReducerFinal = persistGoals
    ? storageAdapter.wrapReducer(goalsReducer, {
        key: 'goals',
        storage: null,
        whitelist: ['goals', 'currentGoal'],
      })
    : goalsReducer;

  const portfolioReducerFinal = persistPortfolio
    ? storageAdapter.wrapReducer(portfolioReducer, {
        key: 'portfolio',
        storage: null,
        whitelist: ['accounts', 'holdings', 'selectedAccountId'],
      })
    : portfolioReducer;

  const userPreferencesReducerFinal = persistUserPreferences
    ? storageAdapter.wrapReducer(userPreferencesReducer, {
        key: 'userPreferences',
        storage: null,
        whitelist: ['preferences'],
      })
    : userPreferencesReducer;

  // Create the store
  const store = configureStore({
    reducer: {
      auth: authReducerFinal,
      goals: goalsReducerFinal,
      portfolio: portfolioReducerFinal,
      userPreferences: userPreferencesReducerFinal,
      ...additionalReducers,
    },
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware(storageAdapter.getMiddlewareConfig()),
  });

  // Create persistor if the adapter supports it (mobile)
  const persistor = storageAdapter.createPersistor?.(store as AppStore);

  return { store, persistor };
}

/**
 * Base store instance for type inference
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _baseStore = configureStore({
  reducer: {
    auth: authReducer,
    goals: goalsReducer,
    portfolio: portfolioReducer,
    userPreferences: userPreferencesReducer,
  },
});

/**
 * Type helpers for store
 */
export type AppStore = typeof _baseStore;
export type RootState = ReturnType<typeof _baseStore.getState>;
export type AppDispatch = typeof _baseStore.dispatch;
